import React, { createContext, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Target, Radio, Calendar, ShoppingBag, TrendingUp, Package, MessageSquare, Sparkles, Zap, Megaphone, Rocket, Layout, Share2, FlaskConical, DollarSign } from 'lucide-react';
import { guidanceAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const ICONS = { target: Target, radio: Radio, calendar: Calendar, shopping: ShoppingBag, trending: TrendingUp, package: Package, message: MessageSquare, sparkles: Sparkles, zap: Zap, megaphone: Megaphone, rocket: Rocket, layout: Layout, share: Share2, flask: FlaskConical, dollar: DollarSign };
const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', dot: 'bg-emerald-400' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20', dot: 'bg-rose-400' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', dot: 'bg-amber-400' },
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', dot: 'bg-blue-400' },
  violet: { bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/20', dot: 'bg-violet-400' },
  red: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', dot: 'bg-red-400' },
};

const SmartNotificationContext = createContext(null);

export function SmartNotificationProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastNotifyRef = useRef(0);

  const triggerSmartNotify = useCallback(async (action, entityType, entityId) => {
    if (!user) return;

    // Throttle: max 1 notification per 3 seconds
    const now = Date.now();
    if (now - lastNotifyRef.current < 3000) return;
    lastNotifyRef.current = now;

    try {
      // Fire smart notify + discovery + attribution in parallel
      const [notifyRes, discoveryRes] = await Promise.all([
        guidanceAPI.getSmartNotify(action, entityType, entityId).catch(() => ({ data: {} })),
        guidanceAPI.triggerDiscovery({ action }).catch(() => ({ data: {} })),
      ]);

      // Also record attribution touch
      guidanceAPI.recordTouch({ entity_type: entityType, entity_id: entityId, source: 'direct', action }).catch(() => {});

      const { impact, suggestions } = notifyRes.data;
      const discovery = discoveryRes.data?.discovery;

      // Show smart notification
      if (impact || (suggestions && suggestions.length > 0)) {
        const suggestion = suggestions?.[0];
        toast.custom((t) => (
          <SmartNotificationToast
            impact={impact}
            suggestion={suggestion}
            onAction={(link) => { toast.dismiss(t); navigate(link); }}
            onDismiss={() => toast.dismiss(t)}
          />
        ), {
          duration: suggestion?.urgency === 'high' ? 8000 : 5000,
          className: 'smart-notify-toast',
        });
      }

      // Show discovery notification (delayed so it doesn't stack)
      if (discovery) {
        setTimeout(() => {
          toast.custom((t) => (
            <DiscoveryToast
              discovery={discovery}
              onAction={(link) => { toast.dismiss(t); navigate(link); }}
              onDismiss={() => toast.dismiss(t)}
            />
          ), { duration: 10000 });
        }, impact ? 3000 : 500);
      }
    } catch {
      // Silent fail
    }
  }, [user, navigate]);

  return (
    <SmartNotificationContext.Provider value={{ triggerSmartNotify }}>
      {children}
    </SmartNotificationContext.Provider>
  );
}

export function useSmartNotify() {
  const ctx = useContext(SmartNotificationContext);
  if (!ctx) return { triggerSmartNotify: () => {} };
  return ctx;
}

function SmartNotificationToast({ impact, suggestion, onAction, onDismiss }) {
  const ImpactIcon = impact ? (ICONS[impact.icon] || Sparkles) : null;
  const SugIcon = suggestion ? (ICONS[suggestion.icon] || Target) : null;
  const colors = suggestion ? (COLOR_MAP[suggestion.color] || COLOR_MAP.emerald) : COLOR_MAP.emerald;

  return (
    <div
      className="w-[340px] bg-zinc-900 border border-zinc-700/60 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden animate-in slide-in-from-right-5 duration-300"
      data-testid="smart-notification"
    >
      {/* Impact line */}
      {impact && (
        <div className="px-4 pt-3 pb-2 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#b8ff00]/10 flex items-center justify-center flex-shrink-0">
            {ImpactIcon && <ImpactIcon size={15} className="text-[#b8ff00]" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-white leading-tight">{impact.text}</p>
            <p className="text-[11px] text-zinc-400 leading-snug">{impact.detail}</p>
          </div>
        </div>
      )}

      {/* Suggestion CTA */}
      {suggestion && (
        <button
          onClick={() => onAction(suggestion.link)}
          className={`w-full px-4 py-3 flex items-center gap-3 transition-all hover:bg-zinc-800/80 ${impact ? 'border-t border-zinc-800' : 'pt-3'}`}
          data-testid="smart-notify-action"
        >
          <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
            {SugIcon && <SugIcon size={14} className={colors.text} />}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className={`text-xs font-bold ${colors.text} leading-tight`}>{suggestion.title}</p>
            <p className="text-[10px] text-zinc-500">{suggestion.subtitle}</p>
          </div>
          <div className={`w-6 h-6 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={colors.text}>
              <path d="M3 1L7 5L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        </button>
      )}

      {/* Dismiss thin bar */}
      <button
        onClick={onDismiss}
        className="w-full py-1.5 text-[9px] text-zinc-600 hover:text-zinc-400 transition-colors uppercase tracking-widest font-bold border-t border-zinc-800/50"
        data-testid="smart-notify-dismiss"
      >
        dismiss
      </button>
    </div>
  );
}


function DiscoveryToast({ discovery, onAction, onDismiss }) {
  const Icon = ICONS[discovery.icon] || Sparkles;
  const colors = COLOR_MAP[discovery.color] || COLOR_MAP.emerald;

  return (
    <div
      className="w-[340px] bg-zinc-900 border border-zinc-700/60 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden animate-in slide-in-from-right-5 duration-300"
      data-testid="discovery-notification"
    >
      {/* Discovery badge */}
      <div className="px-4 pt-3 pb-1 flex items-center gap-2">
        <span className="text-[9px] font-bold uppercase tracking-widest text-[#b8ff00]">New Capability Unlocked</span>
      </div>

      {/* Message */}
      <div className="px-4 pb-2">
        <p className="text-sm text-zinc-300 leading-snug">{discovery.message}</p>
      </div>

      {/* CTA */}
      <button
        onClick={() => onAction(discovery.link)}
        className={`w-full px-4 py-3 flex items-center gap-3 transition-all hover:bg-zinc-800/80 border-t border-zinc-800`}
        data-testid="discovery-action"
      >
        <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon size={14} className={colors.text} />
        </div>
        <span className={`text-xs font-bold ${colors.text}`}>{discovery.cta}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={`ml-auto ${colors.text}`}>
          <path d="M3 1L7 5L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      <button onClick={onDismiss} className="w-full py-1.5 text-[9px] text-zinc-600 hover:text-zinc-400 transition-colors uppercase tracking-widest font-bold border-t border-zinc-800/50">
        dismiss
      </button>
    </div>
  );
}
